/**
 * Horário de funcionamento (config global, Bloco D): store React Query.
 *
 * Contrato: /api/v1/sistema/horario
 *   GET  /                     → { expediente: [...7] }
 *   PUT  /expediente           → { expediente }
 *   GET  /feriados?ano=2026    → { feriados: [...] }
 *   POST /feriados             → { feriado }
 *   PUT  /feriados/:id         → { feriado }
 *   DELETE /feriados/:id       → { ok }
 *   POST /feriados/puxar-nacionais → { criados, atualizados }
 *
 * Segue o padrão do lembrete-config-store (React Query + api client).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, hasToken } from '@/lib/api';

export interface ExpedienteDia {
  /** 0=domingo … 6=sábado */
  diaSemana: number;
  aberto: boolean;
  /** HH:MM */
  inicio: string;
  /** HH:MM */
  fim: string;
}

export type FeriadoTipo = 'nacional' | 'estadual' | 'municipal';
export type FeriadoFonte = 'auto' | 'seed' | 'manual';

export interface Feriado {
  id: string;
  /** 'YYYY-MM-DD' */
  data: string;
  nome: string;
  tipo: FeriadoTipo;
  fonte: FeriadoFonte;
  ativo: boolean;
}

const EXP_KEY = ['v1', 'sistema', 'horario', 'expediente'] as const;
const feriadosKey = (ano: number) => ['v1', 'sistema', 'horario', 'feriados', ano] as const;

/** Nomes dos dias na ordem de exibição (segunda primeiro, domingo por último). */
export const DIAS_ORDEM: Array<{ diaSemana: number; nome: string }> = [
  { diaSemana: 1, nome: 'Segunda' },
  { diaSemana: 2, nome: 'Terça' },
  { diaSemana: 3, nome: 'Quarta' },
  { diaSemana: 4, nome: 'Quinta' },
  { diaSemana: 5, nome: 'Sexta' },
  { diaSemana: 6, nome: 'Sábado' },
  { diaSemana: 0, nome: 'Domingo' },
];

export function useHorarioExpediente() {
  return useQuery({
    queryKey: EXP_KEY,
    queryFn: () => api.get<{ expediente: ExpedienteDia[] }>('/api/v1/sistema/horario'),
    enabled: hasToken(),
    staleTime: 60_000,
    select: (data) => data.expediente,
  });
}

export function useHorarioFeriados(ano: number) {
  return useQuery({
    queryKey: feriadosKey(ano),
    queryFn: () =>
      api.get<{ feriados: Feriado[] }>(`/api/v1/sistema/horario/feriados?ano=${ano}`),
    enabled: hasToken(),
    staleTime: 60_000,
    select: (data) => data.feriados,
  });
}

export function useHorarioActions() {
  const qc = useQueryClient();

  const invalidarFeriados = () =>
    qc.invalidateQueries({ queryKey: ['v1', 'sistema', 'horario', 'feriados'] });

  const salvarExpediente = useMutation({
    mutationFn: (dias: ExpedienteDia[]) =>
      api.put<{ expediente: ExpedienteDia[] }>('/api/v1/sistema/horario/expediente', { dias }),
    onSuccess: (data) => {
      qc.setQueryData(EXP_KEY, data);
      void qc.invalidateQueries({ queryKey: EXP_KEY });
    },
  });

  const puxarNacionais = useMutation({
    mutationFn: (ano: number) =>
      api.post<{ criados: number; atualizados: number }>(
        '/api/v1/sistema/horario/feriados/puxar-nacionais',
        { ano },
      ),
    onSuccess: () => void invalidarFeriados(),
  });

  const criarFeriado = useMutation({
    mutationFn: (body: { data: string; nome: string; tipo: FeriadoTipo }) =>
      api.post<{ feriado: Feriado }>('/api/v1/sistema/horario/feriados', body),
    onSuccess: () => void invalidarFeriados(),
  });

  const editarFeriado = useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      data?: string;
      nome?: string;
      tipo?: FeriadoTipo;
    }) => api.put<{ feriado: Feriado }>(`/api/v1/sistema/horario/feriados/${id}`, body),
    onSuccess: () => void invalidarFeriados(),
  });

  const excluirFeriado = useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>(`/api/v1/sistema/horario/feriados/${id}`),
    onSuccess: () => void invalidarFeriados(),
  });

  return { salvarExpediente, puxarNacionais, criarFeriado, editarFeriado, excluirFeriado };
}
